import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { api } from '../lib/api'

/**
 * The public page behind a share link (01 §3.5 Sharing).
 *
 * A stranger with the link, and nothing else: one watermarked preview, the honesty line, and a
 * way to get in touch. No address, no name, no prices, no session. Everything not needed to
 * look at the picture is a leak waiting for a forwarded message.
 */

interface PublicShare {
  image_url: string
  room: string | null
  disclaimer: string
  business: string
  cta_url: string
}

export function SharedPreview() {
  const { token = '' } = useParams()
  const share = useQuery({
    queryKey: ['share', token],
    queryFn: () => api.get<PublicShare>(`/s/${token}`),
    retry: false,
  })

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-10">
      {share.isPending && <p className="text-muted">Loading…</p>}

      {share.isError && (
        <div className="rounded-card border border-line bg-white p-8 text-center">
          <h1 className="text-2xl">That link has expired</h1>
          <p className="mt-3 text-body">
            Share links last seven days. Ask whoever sent it for a new one.
          </p>
          <a
            href="https://asbhomerenovations.com"
            className="mt-6 inline-block min-h-12 rounded-btn bg-navy px-6 font-semibold leading-[3] text-white"
          >
            ASB Home Renovations
          </a>
        </div>
      )}

      {share.data && (
        <>
          <p className="eyebrow">A preview from the design studio</p>
          <h1 className="mt-4 text-3xl">
            {share.data.room ? `${share.data.room}, reimagined` : 'A room, reimagined'}
          </h1>
          <img
            src={share.data.image_url}
            alt="A preview of the finished room"
            className="mt-6 w-full rounded-img"
          />
          <p className="mt-3 text-sm text-muted">{share.data.disclaimer}</p>

          <div className="mt-8 rounded-card border border-line bg-white p-6">
            <h2 className="text-xl">Want to see your own room like this?</h2>
            <p className="mt-2 text-body">
              {share.data.business} builds bathrooms, kitchens, trim and exteriors across Monmouth
              County. Book a visit and Andrew will price it after he has seen it.
            </p>
            <a
              href={share.data.cta_url}
              className="mt-5 inline-block min-h-12 rounded-btn bg-blue px-6 font-semibold leading-[3] text-white shadow-blue"
            >
              Start with a free estimate
            </a>
          </div>
        </>
      )}
    </div>
  )
}
